import React, { useEffect, useState } from 'react';
import { Loader2, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ExtensionsSettings } from '@/components/extensions/ExtensionsSettings';
import { useCanvasStore } from '@/stores/canvasStore';
import { useExtensionsStore } from '@/stores/extensionsStore';
import { countRecentRuns } from '../jobs';
import { creativeScoutManifest } from './manifest';
import { mutePullPrompt, pullFullPost, usePullPostStore, PULL_POST_ACTION } from './pullPost';

const DAILY_LIMIT = PULL_POST_ACTION.dailyLimit ?? 5;
const GAP = 10;

/** Swallow pointer events so the canvas does not deselect or start a pan. */
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

/**
 * "Pull the full post with Top Creatives?" anchored under a freshly pasted
 * social post. Lives in the canvas transform layer and counter-scales, so it
 * follows the card at any zoom but stays readable.
 */
export function PullPostPrompt({ zoom }: { zoom: number }) {
  const prompt = usePullPostStore((s) => s.prompt);
  const pulling = usePullPostStore((s) => s.pulling);
  const dismiss = usePullPostStore((s) => s.dismiss);
  const item = useCanvasStore((s) => (prompt ? s.items.find((i) => i.id === prompt.itemId) : undefined));
  const { loaded, load, isEnabled } = useExtensionsStore();
  const enabled = isEnabled(creativeScoutManifest.id);
  const [runsUsed, setRunsUsed] = useState<number | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (prompt && !loaded) load();
  }, [prompt, loaded, load]);

  useEffect(() => {
    if (!prompt || !enabled) return;
    let cancelled = false;
    countRecentRuns(creativeScoutManifest.id).then((n) => {
      if (!cancelled) setRunsUsed(n);
    });
    return () => {
      cancelled = true;
    };
  }, [prompt, enabled]);

  // The card was deleted, upgraded or left behind on another board
  if (!prompt || !item || (item.item_type !== 'link' && !pulling[item.id])) {
    return settingsOpen ? <ExtensionsSettings open onOpenChange={setSettingsOpen} /> : null;
  }

  const isPulling = !!pulling[item.id];
  const runsLeft = runsUsed === null ? null : Math.max(0, DAILY_LIMIT - runsUsed);
  const outOfRuns = runsLeft === 0;
  const platform = prompt.social.platformName;

  const handlePull = () => {
    const target = prompt;
    pullFullPost(target).finally(() => {
      if (usePullPostStore.getState().prompt?.itemId === target.itemId) dismiss();
    });
  };

  const handleMute = () => {
    mutePullPrompt(prompt.social.platform);
    dismiss();
  };

  return (
    <>
      <div
        className="absolute pointer-events-auto z-[60]"
        style={{
          left: item.position_x,
          top: item.position_y + item.height + GAP / zoom,
          transform: `scale(${1 / zoom})`,
          transformOrigin: 'top left',
        }}
        onMouseDown={stop}
        onPointerDown={stop}
        onClick={stop}
        onDoubleClick={stop}
        onWheel={stop}
        data-pull-post-prompt="true"
        role="dialog"
        aria-label="Pull the full post"
      >
        <div className="w-[300px] rounded-xl border border-border/70 bg-popover text-popover-foreground shadow-lg p-3 text-sm">
          {isPulling ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              <span>Pulling the full {platform} post…</span>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: creativeScoutManifest.accent }} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium leading-snug">Pull the full post with Top Creatives?</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {!enabled
                      ? 'Top Creatives is off. Turn it on to pull media, caption and engagement.'
                      : outOfRuns
                        ? `You've used all ${DAILY_LIMIT} runs today.`
                        : `Uses 1 of your ${DAILY_LIMIT} runs today${runsLeft !== null ? ` (${runsLeft} left)` : ''}.`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={dismiss}
                  className="text-muted-foreground hover:text-foreground -mr-1 -mt-1 p-1"
                  aria-label="Close"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-center gap-2 mt-3">
                {enabled ? (
                  <Button size="sm" className="h-7 px-3" onClick={handlePull} disabled={outOfRuns}>
                    Pull
                  </Button>
                ) : (
                  <Button size="sm" className="h-7 px-3" onClick={() => setSettingsOpen(true)}>
                    Turn on Top Creatives
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="h-7 px-3" onClick={dismiss}>
                  Not now
                </Button>
              </div>
              <button
                type="button"
                onClick={handleMute}
                className="mt-2 text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              >
                Don't ask again for {platform}
              </button>
            </>
          )}
        </div>
      </div>
      {settingsOpen && <ExtensionsSettings open onOpenChange={setSettingsOpen} />}
    </>
  );
}
